<template>
  <div>
    <v-dialog
      v-if="newAssetDialog"
      v-model="newAssetDialog"
      :fullscreen="$vuetify.breakpoint.smAndDown"
      max-width="1000">
      <EsAssetAdd
        :locations="[location]"
        :selectedLocationId="location.id"
        :serviceCategories="allServiceCategories"
        :assetTypes="allAssetTypes"
        :getManufacturers="getManufacturers"
        :getManufacturerById="getManufacturerById"
        :searchForNewManufacturer="searchForNewManufacturer"
        :createNewManufacturer="createNewManufacturer"
        :getAssetModelNumbers="getAssetModelNumbers"
        :getSignedUrl="getSignedUrl"
        :isSsp="isSsp"
        :showSelector="showSelector"
        @addAsset="createNewAsset($event)"
        @assetSelected="
          $emit('assetSelected', addedAsset);
          newAssetDialog = false;
        "
        @closeNewAssetDialog="newAssetDialog = false" />
    </v-dialog>
    <EsInfrastructurePicker
      v-if="infrastructureDialog"
      :location="location"
      :allAssetTypes="allAssetTypes"
      :allServiceCategories="allServiceCategories"
      :infrastructureOptions="infrastructureOptions"
      @closeInfrastructureDialog="infrastructureDialog = false"
      @assetSelected="$emit('assetSelected', $event)" />
    <v-container fluid>
      <v-row class="justify-center align-center">
        <v-col class="pl-1 pr-1">{{ count }} assets</v-col>
        <v-col cols="12" sm="auto" class="pl-1 pr-1 d-flex justify-end">
          <v-row v-if="!isEditMode">
            <v-col cols="12" sm="auto">
              <EsButtonAction
                @click="openInfrastructureDialog()"
                class="mr-2"
                label="Infrastructure"
                tabindex="-1"
                :block="$vuetify.breakpoint.xsOnly" />
            </v-col>
            <v-col cols="12" sm="auto" v-if="canAddAssets">
              <EsButtonAction
                @click="newAssetDialog = true"
                label="New Asset"
                tabindex="-1"
                :block="$vuetify.breakpoint.xsOnly" />
            </v-col>
          </v-row>
        </v-col>
        <v-col v-if="isEditMode" cols="12">
          <v-alert type="info">
            You can only change the asset to another asset of the same type.
          </v-alert>
        </v-col>
      </v-row>
      <v-row v-if="!isEditMode">
        <v-col cols="12" class="pl-1 pr-1">
          <EsTextField
            v-model="searchQuery"
            @input="search()"
            :autofocus="focusSearch"
            clearable
            hide-details
            label="Search"
            placeholder="Enter an asset name or serial number"
            prepend-inner-icon="mdi-magnify"></EsTextField>
        </v-col>
        <v-col cols="12" class="pl-1 pr-1">
          <div class="text-button">Browse By Category</div>
          <div
            class="horizontal-scroller">
            <v-card
              v-for="category in topLevelCategories"
              :key="category.name"
              class="category-item text-center pa-5"
              :class="{'selected-category' : (selectedServiceCategory || {}).name === category.name}"
              @click="setSelectedCategory(category)">
              <div>
                <v-icon class="d-block">{{ category.icon }}</v-icon>
                <div class="text-caption category-name">{{ category.name }}</div>
              </div>
            </v-card>
            <template v-if="showMoreCategories">
              <v-card
                v-for="category in moreCategories"
                :key="category.name"
                class="category-item text-center pa-5"
                :class="{'selected-category' : (selectedServiceCategory || {}).name === category.name}"
                @click="setSelectedCategory(category)">
                <div>
                  <v-icon class="d-block">{{ category.icon }}</v-icon>
                  <div class="text-caption category-name">{{ category.name }}</div>
                </div>
              </v-card>
            </template>
            <v-card
              class="category-item text-center pa-5"
              @click="showMoreCategories = !showMoreCategories">
              <div>
                <v-icon class="d-block">{{showMoreCategories ? 'mdi-chevron-left-circle-outline' : 'mdi-chevron-right-circle-outline'}}</v-icon>
                <div class="text-caption category-name">{{showMoreCategories ? 'View Less' : 'View More'}}</div>
              </div>
            </v-card>
          </div>
        </v-col>
        <v-col cols="12" class="pt-0" style="height: 54px;"
          v-if="selectedServiceCategory && (selectedServiceCategory.categories || []).length > 1">
          <v-chip
            v-for="subcat in (selectedServiceCategory || {}).categories"
            :key="subcat.id"
            class="mr-2 py-5"
            :class="{'selected-subcategory' : selectedSubServiceCategory === subcat.name}"
            label
            outlined
            @click="selectedSubServiceCategory = subcat.name">
            {{ subcat.name }}
          </v-chip>
        </v-col>
      </v-row>
      <v-row v-if="count === 0 && !isLoading" align="center">
        <v-col>
          <h3>No assets</h3>
        </v-col>
      </v-row>
      <v-row v-if="isLoading">
        <v-col class="d-flex justify-center">
          <v-progress-circular
            color="secondary"
            indeterminate></v-progress-circular>
        </v-col>
      </v-row>
      <v-row v-else>
        <v-col
          class="px-2"
          v-for="asset in assets"
          :key="asset.id"
          cols="12"
          xl="2"
          lg="3"
          md="4"
          sm="6"
          align-self="stretch">
          <EsAssetCard
            :isPartsTown="isPartsTown"
            :class="{ 'pre-selected': preSelectedAssetId === asset.id }"
            :disabled="preSelectedAssetId === asset.id"
            :allServiceCategories="allServiceCategories"
            :getSignedUrl="getSignedUrl"
            @click="$emit('assetSelected', asset)"
            :asset="asset" />
        </v-col>
        <v-col v-if="isLoadingMore" class="px-2 text-center"
          cols="12"
          xl="2"
          lg="3"
          md="4"
          sm="6"
          align-self="stretch">
          <v-progress-circular
            color="secondary"
            indeterminate></v-progress-circular>
        </v-col>
      </v-row>
      <div v-intersect="showMore"></div>
    </v-container>
  </div>
</template>
<script>
import { debounce } from 'lodash';
import {
  EsButtonAction,
  EsTextField,
} from '@86-repairs/design-system';
import EsAssetCard from './EsAssetCard.vue';
import EsAssetAdd from './EsAssetAdd.vue';
import EsInfrastructurePicker from './EsInfrastructurePicker.vue';

export default {
  name: 'EsAssetPicker',
  props: {
    location: {
      type: Object,
      default: () => ({ displayName: '', id: '' }),
    },
    focusSearch: {
      type: Boolean,
      default: false,
    },
    allServiceCategories: {
      type: Array,
      default: () => [],
      required: true,
    },
    serviceCategoryGroups: {
      type: Array,
      default: () => [],
      required: true,
    },
    defaultServiceCategory: {
      type: String, // category id
      default: null,
    },
    allAssetTypes: {
      type: Array,
      default: () => [],
    },
    getSignedUrl: {
      type: Function,
    },
    getManufacturers: {
      type: Function,
      required: true,
    },
    getManufacturerById: {
      type: Function,
      required: true,
    },
    searchForNewManufacturer: {
      type: Function,
    },
    createNewManufacturer: {
      type: Function,
    },
    getAssetModelNumbers: {
      type: Function,
      required: true,
    },
    isSsp: {
      type: Boolean,
      default: false,
    },
    showSelector: {
      type: Boolean,
      default: true,
    },
    isEditMode: {
      type: Boolean,
      required: false,
      default: false,
    },
    preSelectedAssetId: {
      type: String,
      required: false,
    },
    isPartsTown: {
      type: Boolean,
      default: false,
    },
    canAddAssets: {
      type: Boolean,
      default: true,
    },
  },
  components: {
    EsButtonAction,
    EsTextField,
    EsAssetCard,
    EsInfrastructurePicker,
    EsAssetAdd,
  },
  data: () => ({
    page: 0,
    count: 0,
    assets: [],
    searchQuery: null,
    serviceCategories: [],
    isLoading: false,
    isLoadingMore: false,
    addedAsset: {},
    infrastructureDialog: false,
    newAssetDialog: false,
    infrastructureOptions: [],
    showMoreCategories: false,
    selectedServiceCategory: null,
    selectedSubServiceCategory: null,
  }),
  async beforeMount() {
    if (this.defaultServiceCategory) {
      this.serviceCategories = [this.defaultServiceCategory];
    }
    if (this.location) {
      this.getAssets(true);
    }
  },
  computed: {
    queryParams() {
      return {
        offset: this.page * 50,
        limit: 50,
        isInfrastructure: false,
        filterText: this.searchQuery || undefined,
        serviceCategoryIds: this.serviceCategories || undefined,
        locationId: this.location?.id || undefined,
        relations: ['assetType', 'location', 'manufacturer'],
      };
    },
    topLevelCategories() {
      return this.serviceCategoryGroups.filter((c) => !c.showInViewMore);
    },
    moreCategories() {
      return this.serviceCategoryGroups.filter((c) => c.showInViewMore);
    }
  },
  watch: {
    location() {
      this.getAssets(true);
    },
    defaultServiceCategory(value) {
      if (value) {
        this.serviceCategories = [value];
      }
      this.getAssets(true);
    },
    selectedSubServiceCategory(value) {
      if (value) {
        const category = this.allServiceCategories.find((cat) => cat.name === value);
        this.serviceCategories = [category.id];
        this.getAssets(true);
      }
    },
  },
  methods: {
    async getAssets(resetPage) {
      if (resetPage) {
        this.isLoading = true;
        this.page = 1;
        this.$emit('getAssets', {
          params: { ...this.queryParams, offset: 0 },
          done: (result) => this.doneFetchingAssets(result),
        });
      } else {
        this.isLoadingMore = true;
        this.$emit('getAssets', {
          params: this.queryParams,
          done: (result) => this.doneFetchingAssets(result, true),
        });
        this.page += 1;
      }
    },
    doneFetchingAssets(result, addToList) {
      this.assets = addToList ? this.assets.concat(result.data) : result.data;
      this.count = result.count;
      this.isLoading = false;
      this.isLoadingMore = false;
    },
    async showMore(entries) {
      if (entries && entries[0].isIntersecting && this.count > 50 * this.page) {
        this.getAssets(false);
      }
    },
    search: debounce(async function asyncDebounce() {
      this.assets = [];
      this.getAssets(true);
    }, 500),
    openInfrastructureDialog() {
      if (this.location) {
        this.$emit('getInfrastructureByLocationId', {
          locationId: this.location.id,
          done: (result) => this.doneFetchingInfrastructure(result),
        });
      }
    },
    doneFetchingInfrastructure(result) {
      this.infrastructureOptions = result;
      this.infrastructureDialog = true;
    },
    createNewAsset(asset) {
      this.$emit('createNewAsset', {
        asset,
        done: (createdAsset) => {
          this.setNewAsset(createdAsset);
          asset.done();
        },
      });
    },
    setNewAsset(asset) {
      this.addedAsset = asset;
    },
    setSelectedCategory(category) {
      this.selectedServiceCategory = category;
      this.selectedSubServiceCategory = null;
      const allSubIds = category.categories.map((c) => c.id);
      this.serviceCategories = allSubIds;
      this.getAssets(true);
    },
  },
};
</script>

<style scoped lang="scss">
.horizontal-scroller {
  overflow: auto;
  white-space: nowrap;
  display: flex;
}
.category-item {
  min-width: 115px;
  min-height: 115px;
  max-width: 115px;
  max-height: 115px;
  margin: 20px 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  &.selected-category {
    border: 1px solid #44ACC0;
    background: rgba(72, 164, 182, 0.05);
  }
  .category-name {
    line-height: .9rem;
    margin-top: 5px;
  }
}
.v-card--link:before {
  background: #44ACC0;
}
.v-chip.v-chip--outlined.v-chip.v-chip.selected-subcategory {
  border: 1px solid #44ACC0;
  background: rgba(72, 164, 182, 0.05) !important;
}
</style>
